// Surface map: where every simulation cell of a UV atlas lies on a 3D model.
//
// The physical paint engine simulates on a flat grid. For a model, that grid
// is the model's UV atlas: cell (x, y) covers uv ((x + 0.5) / W, (y + 0.5) / H)
// (v grows with y, as in the Canvas 3D paint atlas, which is not flipped).
// Rasterising every UV triangle gives each cell its world position, normal,
// material and UV island, which is what the 3D surface adapter needs to:
//   - map brush stamps from the stroke's tangent plane onto the grid,
//   - continue a stamp across a UV seam onto the neighbouring island,
//   - keep stamps (and the display) inside their own island,
//   - size brushes in model space (texel world size).
//
// Plain JS (no three.js); the 3D viewport supplies world-space triangles.

const NO_CELL = -1;

// meshes: [{ positions: Float32Array (world xyz per vertex), normals?: Float32Array,
//            uvs: Float32Array (uv per vertex), indices?: Uint32Array|Uint16Array,
//            materials?: [{ start, count, material }] (index ranges), material?: number }]
// options: { width, height, conservative = 0.6 (cells of edge tolerance), gutter = 3 }
export function buildSurfaceMap(meshes, { width, height, conservative = 0.6, gutter = 3 } = {}) {
  const W = Math.max(1, width | 0);
  const H = Math.max(1, height | 0);
  const n = W * H;
  const pos = new Float32Array(3 * n);
  const nrm = new Float32Array(3 * n);
  const material = new Int32Array(n).fill(-1);
  const triOf = new Int32Array(n).fill(-1);
  const inside = new Float32Array(n).fill(-Infinity); // signed distance inside the owning triangle (cells)
  let overlapCells = 0;
  let outOfRangeTriangles = 0;
  let triangleCount = 0;
  let degenerateTriangles = 0;
  const overlapMark = new Uint8Array(n);

  const tri = { p: new Float64Array(9), n: new Float64Array(9), uv: new Float64Array(6) };
  const triJac = []; // per triangle: world per grid cell along x (3), along y (3)

  meshes.forEach((mesh) => {
    const P = mesh.positions;
    const N = mesh.normals || null;
    const UV = mesh.uvs;
    if (!P || !UV) return;
    const indexCount = mesh.indices ? mesh.indices.length : P.length / 3;
    const ranges = Array.isArray(mesh.materials) && mesh.materials.length
      ? mesh.materials
      : [{ start: 0, count: indexCount, material: mesh.material ?? 0 }];
    for (const range of ranges) {
      const end = Math.min(indexCount, range.start + range.count);
      for (let t = range.start; t + 2 < end; t += 3) {
        const id = triangleCount;
        triangleCount += 1;
        for (let k = 0; k < 3; k += 1) {
          const v = mesh.indices ? mesh.indices[t + k] : t + k;
          tri.p[3 * k] = P[3 * v]; tri.p[3 * k + 1] = P[3 * v + 1]; tri.p[3 * k + 2] = P[3 * v + 2];
          if (N) { tri.n[3 * k] = N[3 * v]; tri.n[3 * k + 1] = N[3 * v + 1]; tri.n[3 * k + 2] = N[3 * v + 2]; }
          tri.uv[2 * k] = UV[2 * v] * W; tri.uv[2 * k + 1] = UV[2 * v + 1] * H;
        }
        if (!N) {
          const fn = faceNormal(tri.p);
          for (let k = 0; k < 3; k += 1) { tri.n[3 * k] = fn[0]; tri.n[3 * k + 1] = fn[1]; tri.n[3 * k + 2] = fn[2]; }
        }
        const result = rasteriseTriangle(tri, id, range.material ?? 0);
        if (result === 'degenerate') degenerateTriangles += 1;
        else if (result === 'outside') outOfRangeTriangles += 1;
      }
    }
  });

  function rasteriseTriangle(t, id, mat) {
    const [ax, ay, bx, by, cx, cy] = t.uv;
    const area = (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);
    if (!(Math.abs(area) > 1e-9)) return 'degenerate';
    const minX = Math.max(0, Math.floor(Math.min(ax, bx, cx) - conservative));
    const maxX = Math.min(W - 1, Math.ceil(Math.max(ax, bx, cx) + conservative));
    const minY = Math.max(0, Math.floor(Math.min(ay, by, cy) - conservative));
    const maxY = Math.min(H - 1, Math.ceil(Math.max(ay, by, cy) + conservative));
    if (minX > maxX || minY > maxY) return 'outside';
    const sign = area > 0 ? 1 : -1;
    // Exact grid -> world derivatives of this triangle (world per grid cell along x and y).
    const f1x = bx - ax; const f1y = by - ay; const f2x = cx - ax; const f2y = cy - ay;
    const inv = 1 / (f1x * f2y - f2x * f1y);
    for (let a = 0; a < 3; a += 1) {
      const e1 = t.p[3 + a] - t.p[a];
      const e2 = t.p[6 + a] - t.p[a];
      triJac[6 * id + a] = (e1 * f2y - e2 * f1y) * inv;
      triJac[6 * id + 3 + a] = (e2 * f1x - e1 * f2x) * inv;
    }
    // Edge functions normalised to distances in cells.
    const edges = [[bx, by, cx, cy], [cx, cy, ax, ay], [ax, ay, bx, by]].map(([x0, y0, x1, y1]) => {
      const len = Math.hypot(x1 - x0, y1 - y0) || 1;
      return { x0, y0, dx: (x1 - x0) / len, dy: (y1 - y0) / len };
    });
    for (let y = minY; y <= maxY; y += 1) {
      const py = y + 0.5;
      for (let x = minX; x <= maxX; x += 1) {
        const px = x + 0.5;
        let d = Infinity;
        for (const e of edges) d = Math.min(d, sign * (e.dx * (py - e.y0) - e.dy * (px - e.x0)));
        if (d < -conservative) continue;
        const i = y * W + x;
        // Two triangles that both truly contain a cell centre: overlapping UVs
        // (neighbouring triangles only share edges). That cell is one texel for
        // two places on the model, so paint there appears in both.
        if (d > 1e-4 && inside[i] > 1e-4) overlapMark[i] = 1;
        if (d <= inside[i]) continue;
        inside[i] = d;
        triOf[i] = id;
        material[i] = mat;
        // Cells just outside the triangle (conservative cover along island
        // edges) continue the triangle's own linear map, so their positions
        // stay consistent with the grid (seam mapping); normals are clamped.
        const raw = barycentric(t.uv, px, py);
        const w = clampBarycentric(raw);
        for (let a = 0; a < 3; a += 1) {
          pos[3 * i + a] = raw[0] * t.p[a] + raw[1] * t.p[3 + a] + raw[2] * t.p[6 + a];
          nrm[3 * i + a] = w[0] * t.n[a] + w[1] * t.n[3 + a] + w[2] * t.n[6 + a];
        }
        const len = Math.hypot(nrm[3 * i], nrm[3 * i + 1], nrm[3 * i + 2]) || 1;
        nrm[3 * i] /= len; nrm[3 * i + 1] /= len; nrm[3 * i + 2] /= len;
      }
    }
    return 'ok';
  }

  // Texel world size: median distance between neighbouring cells of one triangle.
  const samples = [];
  const stride = Math.max(1, Math.floor(Math.sqrt(n / 60000)));
  for (let y = 0; y < H - 1; y += stride) {
    for (let x = 0; x < W - 1; x += stride) {
      const i = y * W + x;
      if (triOf[i] < 0) continue;
      if (triOf[i + 1] === triOf[i]) samples.push(dist(pos, i, i + 1));
      if (triOf[i + W] === triOf[i]) samples.push(dist(pos, i, i + W));
    }
  }
  samples.sort((a, b) => a - b);
  const texelWorld = samples.length ? samples[Math.floor(samples.length / 2)] : 1;

  // UV islands: neighbouring covered cells that are also neighbours on the
  // model. A UV seam is where neighbouring cells jump apart in world space.
  const parent = new Int32Array(n).fill(-1);
  const find = (i) => {
    let r = i;
    while (parent[r] !== r) r = parent[r];
    while (parent[i] !== r) { const next = parent[i]; parent[i] = r; i = next; }
    return r;
  };
  for (let i = 0; i < n; i += 1) if (triOf[i] >= 0) parent[i] = i;
  const joinLimit = 3.5 * texelWorld;
  const join = (i, j) => {
    if (triOf[j] < 0 || dist(pos, i, j) > joinLimit) return;
    const a = find(i);
    const b = find(j);
    if (a !== b) parent[Math.max(a, b)] = Math.min(a, b);
  };
  for (let y = 0; y < H; y += 1) {
    for (let x = 0; x < W; x += 1) {
      const i = y * W + x;
      if (triOf[i] < 0) continue;
      if (x + 1 < W) join(i, i + 1);
      if (y + 1 < H) join(i, i + W);
    }
  }
  const island = new Uint32Array(n);
  const rootToIsland = new Map();
  const islandCells = [0];
  const islandMaterialVotes = [null];
  for (let i = 0; i < n; i += 1) {
    if (triOf[i] < 0) continue;
    const r = find(i);
    let id = rootToIsland.get(r);
    if (!id) {
      id = islandCells.length;
      rootToIsland.set(r, id);
      islandCells.push(0);
      islandMaterialVotes.push(new Map());
    }
    island[i] = id;
    islandCells[id] += 1;
    const votes = islandMaterialVotes[id];
    votes.set(material[i], (votes.get(material[i]) || 0) + 1);
  }
  const islandMaterial = islandMaterialVotes.map((votes) => {
    if (!votes) return -1;
    let best = -1;
    let bestCount = -1;
    votes.forEach((count, mat) => { if (count > bestCount) { best = mat; bestCount = count; } });
    return best;
  });

  // Gutter: empty cells next to an island copy the nearest island cell when
  // displayed, so texture filtering at island edges never blends in an empty
  // texel (the classic seam line).
  const displaySource = new Int32Array(n).fill(NO_CELL);
  const dsq = new Float32Array(n).fill(Infinity);
  const queue = [];
  for (let i = 0; i < n; i += 1) if (triOf[i] >= 0) { displaySource[i] = i; dsq[i] = 0; queue.push(i); }
  for (let ring = 0; ring < gutter && queue.length; ring += 1) {
    const next = [];
    for (const i of queue) {
      const x = i % W;
      const y = (i / W) | 0;
      const src = displaySource[i];
      const sx = src % W;
      const sy = (src / W) | 0;
      for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]]) {
        if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
        const j = ny * W + nx;
        if (triOf[j] >= 0) continue;
        const d = (nx - sx) * (nx - sx) + (ny - sy) * (ny - sy);
        if (d < dsq[j]) {
          if (dsq[j] === Infinity) next.push(j);
          dsq[j] = d;
          displaySource[j] = src;
        }
      }
    }
    queue.length = 0;
    queue.push(...next);
  }

  for (let i = 0; i < n; i += 1) if (overlapMark[i]) overlapCells += 1;
  const coveredCells = islandCells.reduce((a, b) => a + b, 0);

  const map = {
    width: W,
    height: H,
    pos,
    nrm,
    island,
    material,
    triangle: triOf,
    triJac,
    islandMaterial,
    islandCells,
    displaySource,
    texelWorld,
    stats: {
      triangles: triangleCount,
      degenerateTriangles,
      outOfRangeTriangles,
      coveredCells,
      coverage: coveredCells / n,
      islands: islandCells.length - 1,
      overlapCells,
      overlapFraction: coveredCells ? overlapCells / coveredCells : 0,
    },
  };
  map.hash = buildSpatialHash(map);
  return map;
}

function dist(pos, i, j) {
  return Math.hypot(pos[3 * i] - pos[3 * j], pos[3 * i + 1] - pos[3 * j + 1], pos[3 * i + 2] - pos[3 * j + 2]);
}

function faceNormal(p) {
  const ux = p[3] - p[0]; const uy = p[4] - p[1]; const uz = p[5] - p[2];
  const vx = p[6] - p[0]; const vy = p[7] - p[1]; const vz = p[8] - p[2];
  const nx = uy * vz - uz * vy; const ny = uz * vx - ux * vz; const nz = ux * vy - uy * vx;
  const len = Math.hypot(nx, ny, nz) || 1;
  return [nx / len, ny / len, nz / len];
}

function barycentric(uv, px, py) {
  const [ax, ay, bx, by, cx, cy] = uv;
  const d = (by - cy) * (ax - cx) + (cx - bx) * (ay - cy);
  const w0 = ((by - cy) * (px - cx) + (cx - bx) * (py - cy)) / d;
  const w1 = ((cy - ay) * (px - cx) + (ax - cx) * (py - cy)) / d;
  return [w0, w1, 1 - w0 - w1];
}

function clampBarycentric(w) {
  const a = Math.max(0, w[0]); const b = Math.max(0, w[1]); const c = Math.max(0, w[2]);
  const s = a + b + c || 1;
  return [a / s, b / s, c / s];
}

// ---------------------------------------------------------------- spatial queries

// Coarse world-space hash of covered cells (every `step` cells), used once per
// pointer sample to find every island the brush touches.
export function buildSpatialHash(map, step = 2) {
  const { width: W, height: H, pos, island } = map;
  const bucket = Math.max(1e-9, 8 * map.texelWorld);
  const buckets = new Map();
  for (let y = 0; y < H; y += step) {
    for (let x = 0; x < W; x += step) {
      const i = y * W + x;
      if (!island[i]) continue;
      const key = bucketKey(Math.floor(pos[3 * i] / bucket), Math.floor(pos[3 * i + 1] / bucket), Math.floor(pos[3 * i + 2] / bucket));
      let list = buckets.get(key);
      if (!list) { list = []; buckets.set(key, list); }
      list.push(i);
    }
  }
  return { bucket, buckets, step };
}

function bucketKey(x, y, z) {
  return ((x + 1048576) * 2097152 + (y + 1048576)) * 2097152 + (z + 1048576);
}

// Surface patches within `radius` of world point p, facing roughly like
// `normal`: one entry per island and separate place on the grid (a seam can
// also run inside one island, e.g. where u = 0 meets u = 1 around a cylinder).
// Returns [{ island, count, cell (nearest covered cell), d2 }], largest first.
export function islandsNear(map, p, radius, normal = null) {
  const { hash, pos, nrm, island, width: W } = map;
  const b = hash.bucket;
  const r2 = radius * radius;
  const found = [];
  // Cells further apart than this on the grid belong to different patches.
  const reachCells = 2 * (radius / Math.max(1e-12, map.texelWorld)) + 6;
  const reach2 = reachCells * reachCells;
  const reach = Math.ceil(radius / b);
  const bx = Math.floor(p[0] / b); const by = Math.floor(p[1] / b); const bz = Math.floor(p[2] / b);
  for (let dx = -reach; dx <= reach; dx += 1) {
    for (let dy = -reach; dy <= reach; dy += 1) {
      for (let dz = -reach; dz <= reach; dz += 1) {
        const list = hash.buckets.get(bucketKey(bx + dx, by + dy, bz + dz));
        if (!list) continue;
        for (const i of list) {
          const ex = pos[3 * i] - p[0]; const ey = pos[3 * i + 1] - p[1]; const ez = pos[3 * i + 2] - p[2];
          const d2 = ex * ex + ey * ey + ez * ez;
          if (d2 > r2) continue;
          // The far side of a thin part (or an inner wall) is not under the brush.
          if (normal && nrm[3 * i] * normal[0] + nrm[3 * i + 1] * normal[1] + nrm[3 * i + 2] * normal[2] < 0.2) continue;
          const id = island[i];
          const gx = i % W;
          const gy = (i / W) | 0;
          let entry = null;
          for (const e of found) {
            if (e.island === id && (e.gx - gx) * (e.gx - gx) + (e.gy - gy) * (e.gy - gy) <= reach2) { entry = e; break; }
          }
          if (!entry) { entry = { island: id, count: 0, cell: i, d2, gx, gy }; found.push(entry); }
          entry.count += 1;
          if (d2 < entry.d2) { entry.d2 = d2; entry.cell = i; }
        }
      }
    }
  }
  return found.sort((a, b2) => b2.count - a.count);
}

// World-space derivatives of the cell grid at cell i, within its island:
// columns d(world)/dx and d(world)/dy per cell (central differences where possible).
export function cellJacobian(map, i) {
  // Exact: the derivatives of the triangle the cell belongs to.
  const t = map.triangle ? map.triangle[i] : -1;
  if (t >= 0 && map.triJac && Number.isFinite(map.triJac[6 * t])) {
    const J = map.triJac;
    return { du: [J[6 * t], J[6 * t + 1], J[6 * t + 2]], dv: [J[6 * t + 3], J[6 * t + 4], J[6 * t + 5]] };
  }
  const { width: W, height: H, pos, island } = map;
  const x = i % W;
  const y = (i / W) | 0;
  const id = island[i];
  const diff = (a, b, steps) => [
    (pos[3 * b] - pos[3 * a]) / steps,
    (pos[3 * b + 1] - pos[3 * a + 1]) / steps,
    (pos[3 * b + 2] - pos[3 * a + 2]) / steps,
  ];
  const along = (dxStep, dyStep, limit) => {
    const fwd = [x + dxStep, y + dyStep];
    const back = [x - dxStep, y - dyStep];
    const ok = ([cx, cy]) => cx >= 0 && cy >= 0 && cx < limit[0] && cy < limit[1] && island[cy * W + cx] === id;
    const f = ok(fwd) ? fwd[1] * W + fwd[0] : -1;
    const k = ok(back) ? back[1] * W + back[0] : -1;
    if (f >= 0 && k >= 0) return diff(k, f, 2);
    if (f >= 0) return diff(i, f, 1);
    if (k >= 0) return diff(k, i, 1);
    return null;
  };
  return { du: along(1, 0, [W, H]), dv: along(0, 1, [W, H]) };
}

// 2x2 map from tangent-plane offsets (in cells of `cellWorld`, along unit world
// directions t and b) to grid offsets near cell i: least-squares inverse of
// the cell Jacobian. Returns [m00, m01, m10, m11] (grid = M * plane) or null.
export function planeToGridMatrix(map, i, t, b, cellWorld) {
  const { du, dv } = cellJacobian(map, i);
  if (!du || !dv) return null;
  const a = dot(du, du); const c = dot(du, dv); const d = dot(dv, dv);
  const det = a * d - c * c;
  if (!(det > 1e-20)) return null;
  // grid offset g for world offset w: g = (J^T J)^-1 J^T w
  const solve = (w) => {
    const r0 = dot(du, w); const r1 = dot(dv, w);
    return [(d * r0 - c * r1) / det, (a * r1 - c * r0) / det];
  };
  const gt = solve([t[0] * cellWorld, t[1] * cellWorld, t[2] * cellWorld]);
  const gb = solve([b[0] * cellWorld, b[1] * cellWorld, b[2] * cellWorld]);
  return [gt[0], gb[0], gt[1], gb[1]];
}

function dot(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }

// Nearest covered cell to grid point (gx, gy) within `reach` cells (prefers island `prefer`).
export function nearestCoveredCell(map, gx, gy, reach = 3, prefer = 0) {
  const { width: W, height: H, island } = map;
  const cx = Math.floor(gx); const cy = Math.floor(gy);
  let best = -1;
  let bestScore = Infinity;
  for (let dy = -reach; dy <= reach; dy += 1) {
    for (let dx = -reach; dx <= reach; dx += 1) {
      const x = cx + dx; const y = cy + dy;
      if (x < 0 || y < 0 || x >= W || y >= H) continue;
      const i = y * W + x;
      if (!island[i]) continue;
      const score = dx * dx + dy * dy - (prefer && island[i] === prefer ? 0.5 : 0);
      if (score < bestScore) { bestScore = score; best = i; }
    }
  }
  return best;
}
